import React, { useState, useEffect } from 'react';
import { FaUsers, FaSchool, FaUserGraduate, FaUserTie, FaMoneyBillWave, FaBlog, FaCalendarAlt, FaExclamationTriangle, FaServer } from 'react-icons/fa';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement, PointElement, LineElement } from 'chart.js';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { useToast } from '../../hooks/useToast';
import { useAuth } from '../../hooks/useAuth';
import LoadingSpinner from '../common/LoadingSpinner';

// Register ChartJS components
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement, PointElement, LineElement);

/**
 * Admin dashboard component showing system-wide statistics and analytics
 * @returns {JSX.Element}
 */
const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [revenueData, setRevenueData] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [errors, setErrors] = useState({});
  const { showToast, showError } = useToast();
  const { logout, isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  
  // Check if user is authenticated as admin
  useEffect(() => {
    if (!loading && isAuthenticated && user && user.role !== 'system_admin') {
      showToast('You do not have permission to access the admin dashboard', 'error');
      navigate('/');
    }
  }, [isAuthenticated, user, navigate, showToast, loading]);
  
  // Fetch dashboard data
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setErrors({});
        
        // Fetch dashboard stats
        const response = await api.get('/admin/dashboard');
        
        // Check if we have valid data from the API
        if (response.data && response.data.success && response.data.data) {
          const { stats, revenueData, recentActivity } = response.data.data;
          
          // Track which data pieces are missing
          const missingData = {};
          
          // Only update state if the data is present
          if (stats) {
            setStats(stats);
          } else {
            missingData.stats = true;
          }
          
          if (revenueData) {
            setRevenueData(revenueData);
          } else {
            missingData.revenue = true;
          }
          
          if (recentActivity && recentActivity.length > 0) {
            setRecentActivity(recentActivity);
          } else {
            missingData.activity = true;
          }
          
          console.log('Loaded dashboard data:', response.data.data);
          
          // Set specific errors for missing data
          if (Object.keys(missingData).length > 0) {
            setErrors({
              type: 'incomplete',
              missingData,
              message: 'Some dashboard data is unavailable.'
            });
          }
        } else {
          console.warn('API returned success but no data was found.');
          setErrors({
            type: 'noData',
            message: 'No dashboard data available from the server.',
            allMissing: true
          });
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        
        // Handle authentication errors
        if (error.response && (error.response.status === 401 || error.response.status === 403)) {
          showToast('Not authorized to access admin dashboard. Please log in with an admin account.', 'error');
          
          // Clear token and redirect to login
          await logout();
          navigate('/login', { 
            state: { message: 'Your session has expired or you do not have permission to access the admin dashboard.' } 
          });
          return;
        }
        
        showError('Failed to load dashboard data.');
        
        if (error.response) {
          // Server responded with an error
          setErrors({ 
            type: 'serverError', 
            message: 'Server error: ' + (error.response.data?.message || 'Unable to retrieve data.'),
            allMissing: true
          });
        } else if (error.request) {
          // No response received
          setErrors({ 
            type: 'connectionError', 
            message: 'Cannot connect to the server. Please check your network connection.',
            allMissing: true
          });
        } else {
          // Other error
          setErrors({ 
            type: 'unknownError', 
            message: 'An unexpected error occurred while loading data.',
            allMissing: true
          });
        }
      } finally {
        setLoading(false);
      }
    };
    
    if (isAuthenticated && user && user.role === 'system_admin') {
      fetchDashboardData();
    }
  }, [showToast, showError, logout, navigate, isAuthenticated, user]);
  
  // Render loading spinner
  if (loading) {
    return (
      <div className="h-[600px] flex justify-center items-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }
  
  // Render complete error state only if we have no data at all
  if (errors.allMissing) {
    return (
      <div className="container mx-auto px-4 py-6">
        <h1 className="text-2xl font-bold mb-6">Admin Dashboard</h1>
        
        <div className="bg-white rounded-lg shadow-soft p-8 text-center">
          <FaExclamationTriangle className="text-yellow-500 text-5xl mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">
            {errors.type === 'connectionError' ? 'Connection Error' : 'Data Unavailable'}
          </h2>
          <p className="text-gray-600 mb-4">{errors.message}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="btn-primary inline-flex items-center"
          >
            <FaServer className="mr-2" /> Retry Connection
          </button>
        </div>
      </div>
    );
  }
  
  // Prepare chart data
  const userDistributionData = stats && stats.users ? {
    labels: ['Parents', 'Teachers', 'School Admins', 'Mentors'],
    datasets: [
      {
        data: [
          stats.users.parents || 0,
          stats.users.teachers || 0,
          stats.users.schools || 0,
          stats.users.mentors || 0
        ],
        backgroundColor: [
          'rgba(54, 162, 235, 0.8)',
          'rgba(75, 192, 192, 0.8)',
          'rgba(255, 159, 64, 0.8)',
          'rgba(153, 102, 255, 0.8)',
        ],
        borderColor: [
          'rgba(54, 162, 235, 1)',
          'rgba(75, 192, 192, 1)',
          'rgba(255, 159, 64, 1)',
          'rgba(153, 102, 255, 1)',
        ],
        borderWidth: 1,
      },
    ],
  } : null;
  
  // Revenue chart options and data
  const revenueChartData = revenueData && revenueData.labels ? {
    labels: revenueData.labels,
    datasets: [
      {
        label: 'School Revenue',
        data: revenueData.schoolRevenue || [],
        backgroundColor: 'rgba(75, 192, 192, 0.5)',
        borderColor: 'rgba(75, 192, 192, 1)',
        borderWidth: 1,
      },
      {
        label: 'Parent Revenue',
        data: revenueData.parentRevenue || [],
        backgroundColor: 'rgba(54, 162, 235, 0.5)',
        borderColor: 'rgba(54, 162, 235, 1)',
        borderWidth: 1,
      },
    ],
  } : null;
  
  const revenueChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Monthly Revenue',
        font: {
          size: 16,
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Amount (KES)',
        },
      },
    },
  };
  
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB') + ' ' + date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };
  
  // Main dashboard component
  return (
    <div className="container mx-auto px-4 py-6">
      <h1 className="text-2xl font-bold mb-6">Admin Dashboard</h1>
      
      {/* Global error message if there's a major issue */}
      {errors.type && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <FaExclamationTriangle className="text-yellow-500" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-700">{errors.message}</p>
            </div>
          </div>
        </div>
      )}
      
      {/* Stats Cards Section */}
      <div className="mb-6">
        {errors.missingData?.stats && (
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 mb-4">
            <p className="text-sm text-yellow-700 flex items-center">
              <FaExclamationTriangle className="text-yellow-500 mr-2" />
              Dashboard statistics are unavailable
            </p>
          </div>
        )}
        
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg shadow-soft p-6 flex items-center">
              <div className="rounded-full bg-blue-100 p-3 mr-4">
                <FaUsers className="text-blue-500 text-xl" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Users</p>
                <p className="text-2xl font-bold">{(stats.users?.total || 0).toLocaleString()}</p>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow-soft p-6 flex items-center">
              <div className="rounded-full bg-green-100 p-3 mr-4">
                <FaMoneyBillWave className="text-green-500 text-xl" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Revenue</p>
                <p className="text-2xl font-bold">KES {(stats.subscriptions?.revenue || 0).toLocaleString()}</p>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow-soft p-6 flex items-center">
              <div className="rounded-full bg-yellow-100 p-3 mr-4">
                <FaSchool className="text-yellow-500 text-xl" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Schools</p>
                <p className="text-2xl font-bold">{(stats.users?.schools || 0).toLocaleString()}</p>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow-soft p-6 flex items-center">
              <div className="rounded-full bg-purple-100 p-3 mr-4">
                <FaUserGraduate className="text-purple-500 text-xl" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Active Subscriptions</p>
                <p className="text-2xl font-bold">{(stats.subscriptions?.active || 0).toLocaleString()}</p>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Revenue Chart */}
        <div className="bg-white rounded-lg shadow-soft p-6">
          <h2 className="text-lg font-semibold mb-4">Revenue Overview</h2>
          
          {errors.missingData?.revenue && (
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 mb-4">
              <p className="text-sm text-yellow-700 flex items-center">
                <FaExclamationTriangle className="text-yellow-500 mr-2" />
                Revenue data is unavailable
              </p>
            </div>
          )}
          
          {revenueChartData ? (
            <Bar options={revenueChartOptions} data={revenueChartData} height={100} />
          ) : (
            <div className="flex flex-col items-center justify-center h-64">
              <FaExclamationTriangle className="text-yellow-500 text-3xl mb-2" />
              <p className="text-gray-500">Revenue data unavailable</p>
            </div>
          )}
        </div>
        
        {/* User Distribution */}
        <div className="bg-white rounded-lg shadow-soft p-6">
          <h2 className="text-lg font-semibold mb-4 text-center">User Distribution</h2>
          
          {errors.missingData?.stats && (
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 mb-4">
              <p className="text-sm text-yellow-700 flex items-center">
                <FaExclamationTriangle className="text-yellow-500 mr-2" />
                User statistics are unavailable
              </p>
            </div>
          )}
          
          {userDistributionData ? (
            <>
              <div className="h-64 flex justify-center">
                <Doughnut 
                  data={userDistributionData} 
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                  }}
                />
              </div>
              <div className="grid grid-cols-2 mt-4 gap-2">
                <div className="text-center">
                  <div className="font-medium">Teachers</div>
                  <div className="text-xl font-bold">{(stats.users?.teachers || 0).toLocaleString()}</div>
                </div>
                <div className="text-center">
                  <div className="font-medium">Parents</div>
                  <div className="text-xl font-bold">{(stats.users?.parents || 0).toLocaleString()}</div>
                </div>
                <div className="text-center">
                  <div className="font-medium">School Admins</div>
                  <div className="text-xl font-bold">{(stats.users?.schools || 0).toLocaleString()}</div>
                </div>
                <div className="text-center">
                  <div className="font-medium">Mentors</div>
                  <div className="text-xl font-bold">{(stats.users?.mentors || 0).toLocaleString()}</div>
                </div>
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center h-64">
              <FaExclamationTriangle className="text-yellow-500 text-3xl mb-2" />
              <p className="text-gray-500">User distribution data unavailable</p>
            </div>
          )}
        </div>
      </div>
      
      {/* Second Row of Charts and Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Content Stats */}
        <div className="bg-white rounded-lg shadow-soft p-6">
          <h2 className="text-lg font-semibold mb-4">Content Statistics</h2>
          
          {errors.missingData?.stats && (
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 mb-4">
              <p className="text-sm text-yellow-700 flex items-center">
                <FaExclamationTriangle className="text-yellow-500 mr-2" />
                Content statistics are unavailable
              </p>
            </div>
          )}
          
          {stats ? (
            <div className="space-y-4">
              <div className="flex justify-between items-center pb-2 border-b">
                <div className="flex items-center">
                  <FaBlog className="text-primary mr-2" />
                  <span>Blog Posts</span>
                </div>
                <span className="font-bold">{stats.content?.blogs || 0}</span>
              </div>
              <div className="flex justify-between items-center pb-2 border-b">
                <div className="flex items-center">
                  <FaCalendarAlt className="text-primary mr-2" />
                  <span>Events</span>
                </div>
                <span className="font-bold">{stats.content?.events || 0}</span>
              </div>
              <div className="flex justify-between items-center pb-2 border-b">
                <div className="flex items-center">
                  <FaUserTie className="text-primary mr-2" />
                  <span>Teachers</span>
                </div>
                <span className="font-bold">{stats.users?.teachers || 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <div className="flex items-center">
                  <FaUsers className="text-primary mr-2" />
                  <span>Parents</span>
                </div>
                <span className="font-bold">{stats.users?.parents || 0}</span>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-40">
              <FaExclamationTriangle className="text-yellow-500 text-3xl mb-2" />
              <p className="text-gray-500">Content data unavailable</p>
            </div>
          )}
        </div>
        
        {/* Subscription Stats */}
        <div className="bg-white rounded-lg shadow-soft p-6 lg:col-span-2">
          <h2 className="text-lg font-semibold mb-4">Subscription Overview</h2>
          
          {errors.missingData?.stats && (
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 mb-4">
              <p className="text-sm text-yellow-700 flex items-center">
                <FaExclamationTriangle className="text-yellow-500 mr-2" />
                Subscription statistics are unavailable
              </p>
            </div>
          )}
          
          {stats ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-blue-50 rounded-lg p-4 text-center">
                <p className="text-sm text-gray-600">Total Subscriptions</p>
                <p className="text-2xl font-bold text-blue-600">{(stats.subscriptions?.total || 0).toLocaleString()}</p>
              </div>
              <div className="bg-green-50 rounded-lg p-4 text-center">
                <p className="text-sm text-gray-600">Active Subscriptions</p>
                <p className="text-2xl font-bold text-green-600">{(stats.subscriptions?.active || 0).toLocaleString()}</p>
              </div>
              <div className="bg-red-50 rounded-lg p-4 text-center">
                <p className="text-sm text-gray-600">Expired Subscriptions</p>
                <p className="text-2xl font-bold text-red-600">{(stats.subscriptions?.expired || 0).toLocaleString()}</p>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-40">
              <FaExclamationTriangle className="text-yellow-500 text-3xl mb-2" />
              <p className="text-gray-500">Subscription data unavailable</p>
            </div>
          )}
        </div>
      </div>
      
      {/* Recent Activity */}
      <div className="bg-white rounded-lg shadow-soft p-6">
        <h2 className="text-lg font-semibold mb-4">Recent Activity</h2>
        
        {errors.missingData?.activity && (
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 mb-4">
            <p className="text-sm text-yellow-700 flex items-center">
              <FaExclamationTriangle className="text-yellow-500 mr-2" />
              Recent activity data is unavailable
            </p>
          </div>
        )}
        
        {recentActivity.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Activity
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Details
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date & Time
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {recentActivity.map((activity) => (
                  <tr key={activity.id || activity._id || Math.random().toString()}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {activity.action || 'Unknown Action'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {activity.type === 'user' && `${activity.user || ''} (${activity.role || ''})`}
                      {activity.type === 'school' && `${activity.name || ''} - Admin: ${activity.admin || ''}`}
                      {activity.type === 'subscription' && `${activity.user || ''} - Plan: ${activity.plan || ''}`}
                      {activity.type === 'content' && `${activity.title || ''} by ${activity.author || ''}`}
                      {activity.type === 'payment' && `${activity.amount || ''} from ${activity.user || ''}`}
                      {!['user', 'school', 'subscription', 'content', 'payment'].includes(activity.type) && 
                        (activity.details !== null && typeof activity.details === 'object' 
                          ? JSON.stringify(activity.details) 
                          : String(activity.details || ''))}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {activity.timestamp ? formatDate(activity.timestamp) : 'No date available'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8">
            <FaExclamationTriangle className="text-yellow-500 text-3xl mx-auto mb-2" />
            <p className="text-gray-500">No recent activity available</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
